import { useEffect, useRef, useState } from "react";
import { useScrollStore } from "@/common/store/scrollStore";

interface UseStickySectionOptions {
  activateOffset: number;
  deactivateOffset: number;
  enabled?: boolean;
}

export const useStickySection = ({
  activateOffset,
  deactivateOffset,
  enabled = true,
}: UseStickySectionOptions) => {
  const sentinelRef = useRef<HTMLDivElement>(null);
  const isStickyRef = useRef(false);
  const [isSticky, setIsSticky] = useState(false);
  const scrollElement = useScrollStore((state) => state.scrollElement);

  useEffect(() => {
    if (!scrollElement || !enabled) {
      isStickyRef.current = false;
      setIsSticky(false);
      return;
    }

    const handleScroll = () => {
      const sentinel = sentinelRef.current;

      if (!sentinel) {
        return;
      }

      const { top } = sentinel.getBoundingClientRect();

      if (!isStickyRef.current && top <= activateOffset) {
        isStickyRef.current = true;
        setIsSticky(true);
        return;
      }

      if (isStickyRef.current && top >= deactivateOffset) {
        isStickyRef.current = false;
        setIsSticky(false);
      }
    };

    scrollElement.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();

    return () => {
      scrollElement.removeEventListener("scroll", handleScroll);
    };
  }, [scrollElement, activateOffset, deactivateOffset, enabled]);

  return {
    isSticky,
    sentinelRef,
  };
};
